import React, { useEffect, useMemo, useState } from 'react';
import DocumentTitle from 'react-document-title';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { push, replace } from 'connected-react-router';
import { useQueryClient } from '@tanstack/react-query';
import { usePlayer, usePlayerSurvey, usePlayerQuestionnaires, useTeacherGradingStatus } from 'hooks/usePlayerSurvey';
import { downloadASQReport } from 'actions/download';
import { showPlayerQuestionModal, showQuestionNotesModal, showSurveyNotesModal } from 'actions/internal';
import { isTeachersEnabled, getNotesQuestionnaire } from 'utils/authScopes';
import { getDashboardQuery } from 'selectors';
import { playerLink, linkWithParams } from 'routes';
import TopNavBar from './nav/TopNavBar';
import SubNavBar from './nav/SubNavBar';
import PlayerHeader from 'components/player/PlayerHeader';
import PlayerSurvey from 'components/player/PlayerSurvey';
import { LoadingSpinner } from 'components/helpers/LoadingSpinner';
import AddCompletedASQModalWrapper from 'containers/modals/AddCompletedASQModalWrapper';

const parseSearch = (search) => {
  const params = new URLSearchParams(search);
  return {
    age: params.get('age') ? parseInt(params.get('age'), 10) : null,
    bankId: params.get('bank') ? parseInt(params.get('bank'), 10) : null,
    parentPqId: params.get('parent') ? parseInt(params.get('parent'), 10) : null,
    teacherPqId: params.get('teacher') ? parseInt(params.get('teacher'), 10) : null,
  };
};

const ASQPlayerPage = ({ auth, match, location, dispatch, download, meta }) => {
  const playerId = parseInt(match.params.player_id, 10);
  const queryClient = useQueryClient();
  const [addASQModalOpen, setAddASQModalOpen] = useState(false);

  const { age, bankId, parentPqId, teacherPqId } = parseSearch(location.search);

  const { data: player, isLoading: isLoadingPlayer } = usePlayer(playerId);
  const { data: questionnaires = [], isLoading: isLoadingQuestionnaires } = usePlayerQuestionnaires(playerId);

  // Resolve which group and which parent PQ to show based on URL params
  const currentGroup = useMemo(() => {
    if (!questionnaires.length) return null;
    if (age != null && bankId != null) {
      return questionnaires.find(q => q.ageRange === age && q.bankId === bankId) || null;
    }
    return questionnaires[0] || null;
  }, [questionnaires, age, bankId]);

  const surveyId = useMemo(() => {
    if (!currentGroup) return null;
    if (parentPqId) {
      const found = currentGroup.parents?.find(p => p.value === parentPqId);
      return found ? found.value : currentGroup.value;
    }
    return currentGroup.value;
  }, [currentGroup, parentPqId]);

  const { data: survey, isLoading: isLoadingSurvey } = usePlayerSurvey(surveyId, teacherPqId);

  const showTeacher = isTeachersEnabled(auth);
  const teacherSurveyId = get(survey, 'result.questionnaires.teacher');
  const teacherSurvey = showTeacher && teacherSurveyId
    ? get(survey, `entities.surveys.${teacherSurveyId}`)
    : null;

  const { data: gradingData } = useTeacherGradingStatus(playerId, teacherSurvey);

  // Redirect to most recent group if no age/bank in URL
  useEffect(() => {
    if (!questionnaires.length) return;
    if (age != null && bankId != null) return;
    const first = questionnaires[0];
    if (!first) return;
    const locationFrom = get(location, 'state.from', null);
    dispatch(push(playerLink(playerId, first.ageRange, first.bankId, locationFrom ? { from: locationFrom } : {})));
  }, [questionnaires]);

  // Resolve default parent/teacher PQ IDs and add them to URL when missing
  useEffect(() => {
    if (!currentGroup) return;
    const defaultParent = currentGroup.parents?.[0];
    const defaultTeacher = currentGroup.teachers?.[0];
    if (!defaultParent) return;
    const needsParent = parentPqId == null;
    const needsTeacher = teacherPqId == null && !!defaultTeacher;
    if (!needsParent && !needsTeacher) return;
    const locationFrom = get(location, 'state.from', null);
    const state = locationFrom ? { from: locationFrom } : {};
    const params = new URLSearchParams({ age: currentGroup.ageRange, bank: currentGroup.bankId });
    params.set('parent', needsParent ? defaultParent.value : parentPqId);
    if (defaultTeacher) params.set('teacher', needsTeacher ? defaultTeacher.value : teacherPqId);
    dispatch(replace({
      pathname: linkWithParams('player', { player_id: playerId }),
      search: `?${params.toString()}`,
      state,
    }));
  }, [currentGroup, parentPqId, teacherPqId]);

  // Merge teacher grading result into survey data
  useEffect(() => {
    if (!gradingData || !survey || !surveyId) return;
    queryClient.setQueryData(['playerSurvey', surveyId], (old) => {
      if (!old) return old;
      const updatedSurvey = { ...old.entities.surveys, ...gradingData.entities.surveys };
      const updatedSections = Object.fromEntries(
        Object.entries(old.entities.sections || {}).map(([k, section]) => {
          const newSection = gradingData.entities?.sections?.[k];
          if (!newSection) return [k, section];
          return [k, { ...section, score: { ...section.score, teacher: newSection.score } }];
        })
      );
      return {
        ...old,
        entities: { ...old.entities, surveys: updatedSurvey, sections: updatedSections },
      };
    });
  }, [gradingData]);

  if (!auth.isAuthChecked) return null;
  if (isLoadingPlayer || isLoadingQuestionnaires || isLoadingSurvey || !player || !survey) {
    return (
      <DocumentTitle title="Sparkler">
        <div>
          <TopNavBar />
          <SubNavBar />
          <div className='row'>
            <div className='column u-margin-top--large'>
              <LoadingSpinner bgColor='light' />
            </div>
          </div>
        </div>
      </DocumentTitle>
    );
  }

  const parentSurveyId = get(survey, 'result.questionnaires.parent');
  const parentSurvey = get(survey, `entities.surveys.${parentSurveyId}`);
  const locationFrom = get(location, 'state.from', null);
  const notesQuestionnairePath = getNotesQuestionnaire(auth);
  const playerQuestionnaireId = get(survey, notesQuestionnairePath);
  const webNotesCount = Object.values(get(survey, 'entities.surveys', {}))
    .reduce((sum, s) => sum + (s.notesCount || 0), 0);
  const search = getDashboardQuery(meta);

  const navigateToGroup = (group, pId = null, tId = null) => {
    if (!group) return;
    const state = locationFrom ? { from: locationFrom } : {};
    const params = new URLSearchParams({ age: group.ageRange, bank: group.bankId });
    if (pId) params.set('parent', pId);
    if (tId) params.set('teacher', tId);
    dispatch(push({
      pathname: linkWithParams('player', { player_id: playerId }),
      search: `?${params.toString()}`,
      state,
    }));
  };

  const handleSurveyChange = (selectedOption) => {
    const group = questionnaires.find(q => q.value === selectedOption?.value) || null;
    navigateToGroup(group);
  };

  const handleParentPqChange = (selectedOption) => {
    navigateToGroup(currentGroup, selectedOption?.value, teacherPqId);
  };

  const handleTeacherPqChange = (selectedOption) => {
    navigateToGroup(currentGroup, parentPqId, selectedOption?.value);
  };

  const handleDownloadASQReport = (id, opts = {}) => {
    dispatch(downloadASQReport(id, { ...opts, id }));
  };

  const handleAddASQSuccess = (newSurveyId, administrationDate) => {
    setAddASQModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['playerQuestionnaires', playerId] });
    dispatch(push({
      pathname: linkWithParams('asq', { player_id: playerId, id: newSurveyId }),
      search: administrationDate ? `?date_finished=${administrationDate}` : '',
      state: { from: location },
    }));
  };

  return (
    <DocumentTitle title={`Sparkler - ${player.name || ''}`}>
      <div>
        <TopNavBar withPlayer={true} />
        <SubNavBar />
        <div className='row'>
          <div className="column">
            <PlayerHeader
              auth={auth}
              player={player}
              locationFrom={locationFrom}
              playerQuestionnaireId={playerQuestionnaireId}
              webNotesCount={webNotesCount}
              search={search}
              showSurveyNotesModal={() => dispatch(showSurveyNotesModal(survey, player))}
              onAddCompletedASQ={() => setAddASQModalOpen(true)}
            />
            <PlayerSurvey
              auth={auth}
              player={player}
              survey={survey}
              questionnaires={questionnaires}
              currentGroup={currentGroup}
              selectedTeacherPqId={teacherPqId}
              download={download}
              location={location}
              isLoading={false}
              asqFollowUpEnabled={get(survey, 'result.asqFollowUpEnabled', false)}
              asqFollowUpStatusManagementEnabled={get(survey, 'result.asqFollowUpStatusManagementEnabled', false)}
              teacherOnlyNoContactEnabled={get(survey, 'result.teacherOnlyNoContactEnabled', false)}
              asqFollowUpParentMessagesEnabled={get(survey, 'result.asqFollowUpParentMessagesEnabled', false)}
              onSurveyChange={handleSurveyChange}
              onParentPqChange={handleParentPqChange}
              onTeacherPqChange={handleTeacherPqChange}
              onDownloadASQReport={handleDownloadASQReport}
              onClickNotes={(qId, qNumber, hasNotes) => dispatch(showQuestionNotesModal(qId, qNumber, hasNotes, survey, player))}
              onQuestionInfo={(qId) => dispatch(showPlayerQuestionModal(qId, survey))}
            />
          </div>
        </div>
        <AddCompletedASQModalWrapper
          open={addASQModalOpen}
          onClose={() => setAddASQModalOpen(false)}
          onSuccess={handleAddASQSuccess}
          closeOnOverlayClick={false}
          playerId={playerId}
          player={player}
          location={location}
        />
      </div>
    </DocumentTitle>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  download: state.survey.download,
  meta: state.meta.surveys,
});

export default connect(mapStateToProps)(ASQPlayerPage);
